import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  FileSpreadsheet,
  HelpCircle,
  UploadCloud,
  X,
} from "lucide-react";
import ActivePromotionsBanner from "../../shared/ActivePromotionsBanner";

function matchesSelectedRoute(promo, route) {
  return (
    promo.applicable_to === "all" ||
    promo.applicable_to === "specific_goods" ||
    (promo.applicable_to === "cn_gh" && route === "CN_GH") ||
    (promo.applicable_to === "tr_gh" && route === "TR_GH")
  );
}

export default function AdminUpload() {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ transport_mode: "sea", route: "CN_GH" });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importProgress, setImportProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const [promos, setPromos] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [profileName, setProfileName] = useState("");
  const [selectedProfile, setSelectedProfile] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [mappingChecking, setMappingChecking] = useState(false);
  const mappingRequestRef = useRef(0);
  useEffect(() => {
    axios
      .get("/api/promotions")
      .then((response) => setPromos(response.data))
      .catch(() => {});
    axios
      .get("/api/admin/shipments/profiles")
      .then((response) => setProfiles(response.data))
      .catch(() => {});
  }, []);
  const routePromos = useMemo(
    () => promos.filter((promo) => matchesSelectedRoute(promo, form.route)),
    [form.route, promos],
  );
  const onDrop = useCallback((files, rejectedFiles) => {
    if (rejectedFiles?.length)
      return toast.error("Please choose one Excel file up to 10 MB.");
    if (files[0]) {
      setFile(files[0]);
      setPreview(null);
      setResult(null);
      setImportError(null);
      setStep(1);
      setUploadProgress(100);
      setImportProgress(0);
    }
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });
  const currentMapping = () =>
    Object.fromEntries(
      (preview?.mapping || []).map((item) => [item.source, item.target]),
    );
  const previewFile = async () => {
    if (!file) return toast.error("Select an Excel file first");
    setLoading(true);
    setUploadProgress(0);
    setResult(null);
    setImportError(null);
    const body = new FormData();
    body.append("file", file);
    body.append("transport_mode", form.transport_mode);
    body.append("route", form.route);
    const profile = profiles.find(
      (item) => String(item.id) === String(selectedProfile),
    );
    if (profile?.column_mapping)
      body.append("column_mapping", JSON.stringify(profile.column_mapping));
    try {
      const response = await axios.post("/api/admin/shipments/preview", body, {
        onUploadProgress: (event) => {
          if (event.total)
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
        },
      });
      setUploadProgress(100);
      setPreview(response.data);
      setStep(2);
      if (response.data.ready)
        toast.success("File uploaded and ready to review");
      else toast("Review the mapping and validation messages");
    } catch (error) {
      toast.error(error.response?.data?.message || "Preview failed");
    } finally {
      setLoading(false);
    }
  };
  const changeMapping = (source, target) => {
    if (!preview) return;

    const nextMapping = preview.mapping.map((item) =>
      item.source === source
        ? {
            ...item,
            target,
            label: target
              ? preview.fields.find((field) => field.key === target)?.label
              : "Ignore column",
          }
        : item,
    );
    const nextMappingObject = Object.fromEntries(
      nextMapping.map((item) => [item.source, item.target || null]),
    );
    const missingFields = ["shipping_mark", "tracking_number", "goods_name"].filter(
      (field) => !nextMapping.some((item) => item.target === field),
    );

    // Update the screen immediately, then ask the server to re-run row
    // validation using the manual mapping. This prevents stale errors from
    // the original auto-detection from keeping the import button disabled.
    setPreview((current) => ({
      ...current,
      mapping: nextMapping,
      mappingObject: nextMappingObject,
      missingFields,
    }));

    if (!file) return;
    const requestId = ++mappingRequestRef.current;
    setMappingChecking(true);
    const body = new FormData();
    body.append("file", file);
    body.append("transport_mode", form.transport_mode);
    body.append("route", form.route);
    body.append("column_mapping", JSON.stringify(nextMappingObject));

    axios
      .post("/api/admin/shipments/preview", body)
      .then((response) => {
        if (requestId === mappingRequestRef.current) {
          setPreview(response.data);
          setMappingChecking(false);
        }
      })
      .catch((error) => {
        if (requestId === mappingRequestRef.current) {
          setMappingChecking(false);
          toast.error(error.response?.data?.message || "Could not revalidate mapping");
        }
      });
  };
  const upload = async () => {
    if (!file || !preview)
      return toast.error("Preview the file before importing");
    if (preview.errors?.length || preview.missingFields?.length)
      return toast.error("Resolve the preview issues first");
    setLoading(true);
    setImportProgress(0);
    setResult(null);
    setImportError(null);
    const body = new FormData();
    body.append("file", file);
    body.append("transport_mode", form.transport_mode);
    body.append("route", form.route);
    body.append("column_mapping", JSON.stringify(currentMapping()));
    try {
      const response = await axios.post("/api/admin/shipments/upload", body, {
        onUploadProgress: (event) => {
          if (event.total)
            setImportProgress(
              Math.min(95, Math.round((event.loaded / event.total) * 95)),
            );
        },
      });
      setImportProgress(100);
      setResult(response.data);
      setStep(3);
      toast.success(
        `Imported ${response.data.totalItems} shipments successfully.`,
      );
    } catch (error) {
      setImportError(error.response?.data || { message: "Import failed" });
      toast.error(error.response?.data?.message || "Import failed");
    } finally {
      setLoading(false);
    }
  };
  const saveProfile = async () => {
    if (!profileName.trim() || !preview)
      return toast.error("Enter a profile name after previewing the file");
    try {
      await axios.post("/api/admin/shipments/profiles", {
        name: profileName,
        route: form.route,
        transport_mode: form.transport_mode,
        column_mapping: currentMapping(),
      });
      toast.success("Mapping profile saved");
      setProfileName("");
      const response = await axios.get("/api/admin/shipments/profiles");
      setProfiles(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not save profile");
    }
  };
  const removeFile = (event) => {
    event.stopPropagation();
    setFile(null);
    setPreview(null);
    setResult(null);
    setStep(1);
    setUploadProgress(0);
  };

  return (
    <div>
      <div className="page-header">
        <Link to="/admin" className="back-btn" aria-label="Back to dashboard">
          &lt;
        </Link>
        <h1>Upload Shipping Sheet</h1>
        <button
          type="button"
          className="page-help-button page-help-button--header"
          onClick={() => setShowHelp((current) => !current)}
          aria-label="Upload help"
        >
          <HelpCircle size={18} />
          <span>How it works</span>
        </button>
      </div>
      {showHelp && (
        <div className="page-help-panel page-help-panel--content">
          <strong>How this upload process works</strong>
          <p>
            Complete the three steps in order: prepare the workbook, review its
            detected columns, then confirm the import. The system identifies
            customers by shipping mark and applies the selected route, transport
            mode, and configured fees.
          </p>
        </div>
      )}
      <div className="page-content upload-wizard-content">
        <div className="upload-stepper">
          {[
            ["1", "Prepare file"],
            ["2", "Preview & map"],
            ["3", "Complete"],
          ].map(([number, label], index) => (
            <React.Fragment key={number}>
              <div
                className={`upload-step ${step === index + 1 ? "active" : ""} ${step > index + 1 ? "complete" : ""}`}
              >
                <span>
                  {step > index + 1 ? <CheckCircle2 size={16} /> : number}
                </span>
                <strong>{label}</strong>
              </div>
              {index < 2 && (
                <i className={step > index + 1 ? "complete" : ""} />
              )}
            </React.Fragment>
          ))}
        </div>
        {step === 1 && (
          <>
            <div className="card upload-instructions">
              <p className="section-title">Step 1 · Prepare your workbook</p>
              <p className="helper-text">
                Choose the route and transport mode, then select the client
                spreadsheet. The file is checked before you move to column
                mapping.
              </p>
              <div className="upload-settings-grid">
                <div className="input-group">
                  <label>Route</label>
                  <select
                    className="input-field"
                    value={form.route}
                    onChange={(event) =>
                      setForm({ ...form, route: event.target.value })
                    }
                  >
                    <option value="CN_GH">China to Ghana</option>
                    <option value="TR_GH">Turkey to Ghana</option>
                  </select>
                  <small className="helper-text">
                    Selects the warehouse and pricing configuration.
                  </small>
                </div>
                <div className="input-group">
                  <label>Transport mode</label>
                  <select
                    className="input-field"
                    value={form.transport_mode}
                    onChange={(event) =>
                      setForm({ ...form, transport_mode: event.target.value })
                    }
                  >
                    <option value="sea">Sea freight — billed by CBM</option>
                    <option value="air">Air freight — billed by KG</option>
                  </select>
                  <small className="helper-text">
                    Controls whether pricing uses volume or weight.
                  </small>
                </div>
              </div>
              {profiles.length > 0 && (
                <div className="input-group" style={{ marginBottom: 0 }}>
                  <label>Saved mapping profile (optional)</label>
                  <select
                    className="input-field"
                    value={selectedProfile}
                    onChange={(event) => setSelectedProfile(event.target.value)}
                  >
                    <option value="">Auto-detect this file</option>
                    {profiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {routePromos.length > 0 && (
              <ActivePromotionsBanner
                promotions={routePromos}
                audience="upload"
                className="upload-promo-banner"
              />
            )}
            <div
              {...getRootProps()}
              className={`shipment-dropzone ${isDragActive ? "active" : ""} ${file ? "has-file" : ""}`}
            >
              <input {...getInputProps()} />
              {file ? (
                <div className="shipment-file-state">
                  <span className="shipment-file-icon">
                    <FileSpreadsheet size={25} />
                  </span>
                  <span className="shipment-file-copy">
                    <strong>{file.name}</strong>
                    <small>
                      {(file.size / 1024 / 1024).toFixed(2)} MB · File ready
                    </small>
                  </span>
                  <button
                    type="button"
                    className="shipment-file-remove"
                    aria-label="Remove selected file"
                    onClick={removeFile}
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <div className="shipment-drop-content">
                  <span className="shipment-drop-icon">
                    <UploadCloud size={29} />
                  </span>
                  <strong>
                    {isDragActive
                      ? "Drop your workbook here"
                      : "Drop your workbook here"}
                  </strong>
                  <p>
                    or <span>browse from your device</span>
                  </p>
                  <small>
                    Excel workbooks only · XLSX or XLS · Maximum 10 MB
                  </small>
                </div>
              )}
            </div>
            {file && (
              <div className="upload-progress-card">
                <div>
                  <strong>File ready</strong>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="upload-progress-track">
                  <i style={{ width: `${uploadProgress}%` }} />
                </div>
                <small>
                  Your workbook is ready for the preview and mapping stage.
                </small>
              </div>
            )}
            <button
              className="btn btn-primary btn-full upload-next-button"
              onClick={previewFile}
              disabled={loading || !file}
            >
              {loading ? (
                <>
                  <span className="spinner" /> Uploading file...
                </>
              ) : (
                <>
                  Next: preview and map <ArrowRight size={17} />
                </>
              )}
            </button>
          </>
        )}
        {step === 2 && preview && (
          <>
            <div className="upload-stage-heading">
              <div>
                <span className="upload-stage-kicker">Step 2</span>
                <h2>Preview and map columns</h2>
                <p>
                  Confirm that each spreadsheet column points to the right
                  system field before importing.
                </p>
              </div>
              <button
                type="button"
                className="upload-back-button"
                onClick={() => setStep(1)}
              >
                <ArrowLeft size={15} /> Back
              </button>
            </div>
            <div className="upload-progress-card upload-progress-card--active">
              <div>
                <strong>Workbook uploaded</strong>
                <span>100%</span>
              </div>
              <div className="upload-progress-track">
                <i style={{ width: "100%" }} />
              </div>
              <small>
                {preview.totalRows} rows detected · {preview.worksheet}
              </small>
            </div>
            {preview.pricing && (
              <div className="card upload-pricing-confirmation">
                <div>
                  <span className="upload-stage-kicker">Pricing confirmation</span>
                  <strong>{form.transport_mode === "sea" ? "Sea freight · CBM billing" : "Air freight · KG billing"}</strong>
                  <small>Only the selected measurement is used. Quantity is not multiplied.</small>
                </div>
                <div className="upload-pricing-values">
                  <span><small>Route</small><strong>{form.route === "CN_GH" ? "China → Ghana" : "Turkey → Ghana"}</strong></span>
                  <span><small>Rate</small><strong>${Number(preview.pricing.selectedRateUsd).toFixed(2)} / {preview.pricing.selectedBasis}</strong></span>
                  <span><small>Exchange</small><strong>{Number(preview.pricing.exchangeRate).toFixed(4)} GHS/USD</strong></span>
                  <span><small>Effective GHS rate</small><strong>GHS {Number(preview.pricing.selectedRateGhs).toFixed(2)} / {preview.pricing.selectedBasis}</strong></span>
                </div>
              </div>
            )}
            <div className="card upload-mapping-card">
              <div className="upload-mapping-heading">
                <div>
                  <p className="section-title">Column mapping</p>
                  <p className="helper-text">
                    Ignore columns that are not needed. Required fields must be
                    mapped before continuing.
                  </p>
                  {mappingChecking && (
                    <p className="helper-text">Rechecking the rows with your mapping…</p>
                  )}
                </div>
              </div>
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Spreadsheet column</th>
                      <th>System field</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.mapping.map((item) => (
                      <tr key={item.source}>
                        <td>{item.source}</td>
                        <td>
                          <select
                            className="input-field"
                            value={item.target || ""}
                            onChange={(event) =>
                              changeMapping(item.source, event.target.value)
                            }
                          >
                            <option value="">Ignore column</option>
                            {preview.fields.map((field) => (
                              <option key={field.key} value={field.key}>
                                {field.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.errors?.length > 0 && (
                <div className="notice notice-error">
                  <strong>{preview.errors.length} validation issue(s)</strong>
                  <p>
                    {preview.errors
                      .slice(0, 5)
                      .map((error) => `Row ${error.row}: ${error.message}`)
                      .join(" · ")}
                  </p>
                </div>
              )}
              {preview.missingFields?.length > 0 && (
                <div className="notice notice-error">
                  <strong>Required fields are not mapped</strong>
                  <p>
                    {preview.missingFields
                      .map(
                        (field) =>
                          preview.fields.find((item) => item.key === field)
                            ?.label,
                      )
                      .join(", ")}
                  </p>
                </div>
              )}
              {preview.errors?.length === 0 &&
                preview.missingFields?.length === 0 && (
                  <div className="notice notice-success">
                    <strong>{preview.totalRows} rows ready.</strong>
                    <p>
                      Customer matching and pricing will use the selected route,
                      mode, and current configuration.
                    </p>
                  </div>
                )}
              <div className="upload-profile-save">
                <input
                  className="input-field"
                  value={profileName}
                  onChange={(event) => setProfileName(event.target.value)}
                  placeholder="Save this mapping as a reusable profile"
                />
                <button className="btn btn-secondary" onClick={saveProfile}>
                  Save profile
                </button>
              </div>
              {importError && (
                <div className="notice notice-error upload-import-error">
                  <strong>{importError.message}</strong>
                  {importError.code === "DUPLICATE_UPLOAD" && importError.duplicateUpload && (
                    <p>
                      Existing batch #{importError.duplicateUpload.id} · {importError.duplicateUpload.total_items} records · {importError.duplicateUpload.filename}
                    </p>
                  )}
                  {importError.duplicates?.length > 0 && (
                    <ul>
                      {importError.duplicates.slice(0, 8).map((duplicate, index) => (
                        <li key={`${duplicate.itemId || "duplicate"}-${index}`}>
                          Row {duplicate.row || "?"}{duplicate.trackingNumber ? ` · ${duplicate.trackingNumber}` : ""}: {duplicate.reason}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <button
              className="btn btn-primary btn-full upload-next-button"
              onClick={upload}
              disabled={
                loading ||
                mappingChecking ||
                preview.errors?.length > 0 ||
                preview.missingFields?.length > 0
              }
            >
              {loading ? (
                <>
                  <span className="spinner" /> Importing {importProgress}%
                </>
              ) : (
                <>
                  Confirm and import <ArrowRight size={17} />
                </>
              )}
            </button>
            {loading && (
              <div className="upload-progress-card upload-progress-card--import">
                <div>
                  <strong>Importing shipments</strong>
                  <span>{importProgress}%</span>
                </div>
                <div className="upload-progress-track">
                  <i style={{ width: `${importProgress}%` }} />
                </div>
                <small>
                  Validating rows, identifying customers, and applying
                  configured fees.
                </small>
              </div>
            )}
          </>
        )}
        {step === 3 && result && (
          <div className="upload-complete-card">
            <span className="upload-complete-icon">
              <CheckCircle2 size={28} />
            </span>
            <span className="upload-stage-kicker">Step 3 · Complete</span>
            <h2>Import finished successfully</h2>
            <p>
              {result.totalItems} shipment records were processed and the
              configured customer matching and pricing workflow has completed.
            </p>
            <div className="upload-complete-summary">
              <div>
                <small>Batch ID</small>
                <strong>#{result.uploadId}</strong>
              </div>
              <div>
                <small>Records imported</small>
                <strong>{result.totalItems}</strong>
              </div>
              <div>
                <small>Progress</small>
                <strong>100%</strong>
              </div>
            </div>
            {result.appliedPromotions?.length > 0 && (
              <p className="helper-text">
                {result.appliedPromotions.length} promotion(s) were applied.
              </p>
            )}
            <Link to="/admin/batches" className="btn btn-primary">
              View shipment batches <ArrowRight size={17} />
            </Link>
            <button
              type="button"
              className="upload-new-button"
              onClick={() => {
                setStep(1);
                setFile(null);
                setPreview(null);
                setResult(null);
                setUploadProgress(0);
              }}
            >
              Upload another sheet
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
